/**
 * Estilos residuais do construtor de Formularios.
 *
 * Filosofia (igual ao CRM): o grosso da UI usa classes canonicas
 * (.btn / .badge / .input / .select / .textarea / .input-label / .table-wrap
 * / .dtable / .empty / .so-banner). Aqui ficam SO os layouts proprios do
 * builder que nao tem equivalente canonico: as 3 zonas do editor, o canvas
 * de campos arrastaveis, a palette de tipos, o grid do FormRenderer e os
 * blocos da pagina publica.
 *
 * Tokens via `var(--...)` direto (mesma abordagem de styles/shared.jsx) —
 * garante dark mode correto sem depender de nomes de chave do theme.
 */
import styled from "styled-components";

// ============================================================
// NAV DO FORMULARIO (Editor / Respostas / Dashboard)
// ============================================================

// Faixa de abas entre as 3 visoes de um formulario. Segue o padrao de
// tabchips do app (cor so comunica o estado ativo via accent).
export const FormTabsBar = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  border-bottom: 1px solid var(--border-subtle);
  margin-bottom: 16px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const FormTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  border: 0;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? "var(--accent)" : "transparent")};
  background: transparent;
  color: ${({ $active }) => ($active ? "var(--accent)" : "var(--ink-3)")};
  font: inherit;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    color: ${({ $active }) => ($active ? "var(--accent)" : "var(--ink)")};
  }
  svg {
    width: 14px;
    height: 14px;
  }
`;

// ============================================================
// DASHBOARD (metricas por formulario)
// ============================================================

// Grid responsivo dos cards de grafico (1-2 colunas, sem scroll lateral).
export const DashGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
  gap: 16px;
`;

// Card individual de grafico/metrica do dashboard (segue .panel).
export const DashCard = styled.section`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  padding: 16px 18px;
  min-width: 0;

  > h3 {
    font-family: var(--font-display);
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 4px;
  }
  > .dash-card__sub {
    font-size: 12px;
    color: var(--ink-3);
    margin: 0 0 14px;
  }
`;

// Barra horizontal de distribuicao de opcao (label + track + count).
export const DistRow = styled.div`
  display: grid;
  grid-template-columns: minmax(80px, 30%) 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 5px 0;
  font-size: 13px;
  min-width: 0;

  > .dist-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--ink-2);
  }
  > .dist-track {
    height: 14px;
    background: var(--surface-2);
    border-radius: 999px;
    overflow: hidden;
  }
  > .dist-track > span {
    display: block;
    height: 100%;
    border-radius: 999px;
  }
  > .dist-count {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: var(--ink);
    min-width: 40px;
    text-align: right;
  }
`;

// Linha de "preenchimento por campo" (label + barra de % + valor).
export const FieldFillRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px auto;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--divider);
  min-width: 0;

  &:last-child {
    border-bottom: 0;
  }
  > .fill-name {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  > .fill-name > .nm {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: var(--ink);
  }
  > .fill-track {
    height: 8px;
    background: var(--surface-2);
    border-radius: 999px;
    overflow: hidden;
  }
  > .fill-track > span {
    display: block;
    height: 100%;
    background: var(--accent);
    border-radius: 999px;
  }
  > .fill-val {
    font-size: 12px;
    color: var(--ink-3);
    font-variant-numeric: tabular-nums;
    text-align: right;
    white-space: nowrap;
  }
`;

// ============================================================
// RESPOSTAS — detalhe de uma submissao (SlideOverPanel)
// ============================================================

export const RespostaDetalheList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;

  > .row {
    display: grid;
    grid-template-columns: minmax(120px, 38%) 1fr;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid var(--divider);
    min-width: 0;
  }
  > .row:last-child {
    border-bottom: 0;
  }
  dt {
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-3);
    min-width: 0;
    word-break: break-word;
  }
  dd {
    margin: 0;
    font-size: 13px;
    color: var(--ink);
    min-width: 0;
    word-break: break-word;
    white-space: pre-wrap;
  }
  dd.muted {
    color: var(--ink-4);
  }
`;

// ============================================================
// LISTA
// ============================================================

export const PublicLinkCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-3);
  min-width: 0;

  > .url {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
  }
`;

// Botao "copiar" / "abrir" compacto inline na tabela (icone only).
export const InlineIconButton = styled.button`
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-3);
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--surface-2);
    color: var(--ink);
  }
  svg {
    width: 13px;
    height: 13px;
  }
`;

// Toggle ativo/inativo (switch) — cor comunica estado (on=success).
export const Switch = styled.button`
  --w: 36px;
  --h: 20px;
  position: relative;
  width: var(--w);
  height: var(--h);
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: ${({ $on }) => ($on ? "var(--success-solid)" : "var(--surface-3)")};
  border-color: ${({ $on }) =>
    $on ? "var(--success-solid)" : "var(--border-strong)"};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  flex-shrink: 0;
  padding: 0;

  &::after {
    content: "";
    position: absolute;
    top: 1px;
    left: ${({ $on }) => ($on ? "17px" : "1px")};
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: #fff;
    transition: left 0.15s ease;
    box-shadow: var(--shadow-1);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ============================================================
// EDITOR — 3 zonas
// ============================================================

export const EditorLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr minmax(320px, 380px);
  gap: 16px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const EditorMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

// Coluna do preview ao vivo (sticky no desktop para acompanhar a rolagem).
export const EditorAside = styled.aside`
  position: sticky;
  top: 16px;
  min-width: 0;

  @media (max-width: 980px) {
    position: static;
  }
`;

// Card generico do editor (config / canvas / preview) — segue .panel.
export const EditorCard = styled.section`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  overflow: hidden;
`;

export const EditorCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border-subtle);

  > h2 {
    font-family: var(--font-display);
    font-size: 14px;
    font-weight: 600;
    margin: 0;
  }
`;

export const EditorCardBody = styled.div`
  padding: 16px 18px;
`;

// Grid de settings (label em cima do controle, 2 colunas).
export const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  > .full {
    grid-column: 1 / -1;
  }
  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

// Linha de cor do tema (color picker + texto).
export const ColorRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  input[type="color"] {
    width: 38px;
    height: 30px;
    padding: 0;
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-sm);
    background: var(--surface);
    cursor: pointer;
  }
  > .hex {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--ink-3);
  }
`;

// ============================================================
// CANVAS de campos arrastaveis
// ============================================================

export const CamposLista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CampoRow = styled.div`
  display: grid;
  grid-template-columns: 18px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  background: var(--surface);
  border: 1px solid ${({ $secao }) => ($secao ? "var(--accent)" : "var(--border)")};
  border-radius: var(--radius-md);
  ${({ $secao }) =>
    $secao
      ? "border-style: dashed; background: var(--accent-soft);"
      : ""}

  &:hover {
    border-color: var(--border-strong);
    box-shadow: var(--shadow-1);
  }
`;

export const DragHandle = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  color: var(--ink-4);
  cursor: grab;
  display: grid;
  place-items: center;

  &:active {
    cursor: grabbing;
  }
  svg {
    width: 15px;
    height: 15px;
  }
`;

export const CampoInfo = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  background: transparent;
  border: 0;
  padding: 0;
  min-width: 0;
  cursor: pointer;
  font: inherit;

  > .nm {
    font-size: 13px;
    font-weight: 500;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  > .meta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--ink-3);
    font-family: var(--font-mono);
  }
`;

export const CampoActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

// ============================================================
// PALETTE de tipos (adicionar campo)
// ============================================================

export const PaletteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

export const PaletteItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--surface);
  color: var(--ink-2);
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  text-align: left;

  &:hover {
    border-color: var(--accent);
    background: var(--accent-soft);
    color: var(--accent);
  }
  svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }
`;

// ============================================================
// CAMPO EDITOR PANEL (SlideOverPanel) — editor de opcoes + condicoes
// ============================================================

export const PanelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const PanelLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-2);
`;

export const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const CheckLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--ink);
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
  }
`;

// Editor de lista de opcoes (SELECT/RADIO/...).
export const OpcaoRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: center;
`;

// Bloco da logica condicional (fundo levemente destacado).
export const CondicaoBox = styled.div`
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: 12px;
  background: var(--surface-2);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const RegraRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 6px;
  align-items: center;

  @media (max-width: 520px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const SubLabel = styled.div`
  font-family: var(--font-mono);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-3);
`;

// Divisor de secao dentro do painel.
export const PanelSectionTitle = styled.div`
  font-family: var(--font-display);
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  border-top: 1px solid var(--divider);
  margin-top: 4px;
  padding-top: 14px;
`;

// ============================================================
// FORM RENDERER (preview + publico) — grid de campos renderizados
// Escopado em .fb-render para nao vazar nomes genericos.
// ============================================================

// As variaveis --fb-* vem de designConfig.js (varsDoDesign) via style do wrapper;
// sem elas, os fallbacks reproduzem o visual de antes da aba Design.
export const RenderScope = styled.div`
  .fb-render__grid {
    display: grid;
    grid-template-columns: repeat(var(--fb-cols, 2), minmax(0, 1fr));
    gap: var(--fb-gap, 14px);
  }
  @media (max-width: 560px) {
    .fb-render__grid {
      grid-template-columns: repeat(var(--fb-cols-mobile, 1), minmax(0, 1fr));
    }
  }
  .input, .select, .textarea {
    border-radius: var(--fb-input-radius, var(--radius-md));
    background: var(--fb-input-bg, var(--surface));
  }
  .fb-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
  }
  .fb-field--full {
    grid-column: 1 / -1;
  }
  .fb-field__label {
    font-weight: 500;
    color: var(--fb-label-color, var(--ink-2));
  }
  .fb-req {
    color: var(--danger-text);
  }
  .fb-field__error {
    font-size: 11px;
    color: var(--danger-text);
  }
  .fb-field__aviso--warning {
    color: var(--warning-text);
  }
  .fb-field--secao {
    margin-top: 4px;
  }
  .fb-secao__title {
    font-family: var(--font-display);
    font-size: 15px;
    font-weight: 600;
    color: var(--ink);
    margin: 8px 0 0;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--divider);
  }
  .fb-secao__sub {
    font-size: 12px;
    color: var(--ink-3);
    margin: 4px 0 0;
  }
  .fb-choices {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .fb-choice {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--ink);
    cursor: pointer;
  }
  .fb-choice input {
    width: 16px;
    height: 16px;
    accent-color: var(--accent);
  }
  .fb-render__foot {
    margin-top: 18px;
    display: flex;
    justify-content: var(--fb-btn-justify, flex-start);
  }
  .fb-render__foot .btn {
    width: var(--fb-btn-width, auto);
    justify-content: center;
    border-radius: var(--fb-btn-radius, var(--radius-md));
  }
  .fb-render__foot .btn--primary {
    background: var(--fb-btn-bg, var(--accent));
    color: var(--fb-btn-color, var(--ink-on-accent));
  }
  .fb-render__foot .btn--primary:hover:not(:disabled) {
    filter: brightness(0.94);
  }
`;

// Moldura de preview (simula a pagina publica em miniatura).
export const PreviewFrame = styled.div`
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg);
  padding: 18px;
  max-height: 70vh;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--border-strong) transparent;
`;

export const PreviewHeader = styled.div`
  margin-bottom: 16px;

  > .titulo {
    font-family: var(--font-display);
    font-size: 18px;
    font-weight: 600;
    color: var(--ink);
    margin: 0;
  }
  > .sub {
    font-size: 13px;
    color: var(--ink-3);
    margin-top: 4px;
  }
`;

// ============================================================
// PAGINA PUBLICA (/f/:slug)
// ============================================================

// As variaveis --pub-* vem de designConfig.js (varsDoDesign) via `style` da
// PublicPage; sem elas os fallbacks reproduzem o visual original. Em
// `$preview` (aba Design) a pagina nao forca 100vh, e `data-viewport=
// "mobile"` liga as regras de celular sem depender da largura da janela.
export const PublicPage = styled.div`
  --pub-accent: ${({ $accent }) => $accent || "var(--accent)"};
  min-height: ${({ $preview }) => ($preview ? "100%" : "100vh")};
  background-color: var(--pub-bg, var(--bg));
  background-image: var(--pub-bg-image, none);
  background-size: var(--pub-bg-size, cover);
  background-repeat: var(--pub-bg-repeat, no-repeat);
  background-position: center top;
  color: var(--pub-text-color, var(--ink));
  font-family: var(--pub-font, inherit);
  font-size: var(--pub-font-size, 14px);
  padding: 0 0 48px;

  &[data-viewport="mobile"] main {
    margin: var(--pub-mobile-pad, 16px);
    padding: 20px;
    max-width: none;
  }
  &[data-viewport="mobile"] .fb-render__grid {
    grid-template-columns: repeat(var(--fb-cols-mobile, 1), minmax(0, 1fr));
  }
`;

export const PublicTopbar = styled.header`
  display: var(--pub-topbar, block);
  height: 8px;
  background: var(--pub-accent);
`;

export const PublicCard = styled.main`
  max-width: var(--pub-card-w, 640px);
  margin: var(--pub-page-pad, 32px) auto 0;
  background: var(--pub-card-bg, var(--surface));
  border: var(--pub-card-border, 1px solid var(--border));
  border-radius: var(--pub-card-radius, var(--radius-2xl));
  box-shadow: var(--pub-card-shadow, var(--shadow-2));
  padding: var(--pub-card-pad, 28px);

  @media (max-width: 640px) {
    margin: var(--pub-mobile-pad, 16px);
    padding: 20px;
  }
`;

export const PublicLogo = styled.div`
  text-align: var(--pub-logo-align, left);
  margin-bottom: 16px;

  > img {
    height: var(--pub-logo-h, 48px);
    max-width: 100%;
    object-fit: contain;
  }
`;

export const PublicBanner = styled.div`
  margin: -4px 0 18px;

  > img {
    display: block;
    width: 100%;
    border-radius: calc(var(--pub-card-radius, 16px) / 2);
    object-fit: cover;
  }
`;

export const PublicTitle = styled.h1`
  font-family: var(--pub-font, var(--font-display));
  font-size: var(--pub-title-size, 24px);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
  color: var(--pub-title-color, var(--ink));
`;

export const PublicSub = styled.p`
  font-size: 1em;
  color: var(--pub-text-color, var(--ink-3));
  margin: 8px 0 22px;
  line-height: 1.5;
`;

export const PublicFooter = styled.p`
  margin: 18px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--pub-text-color, var(--ink-3));
  white-space: pre-wrap;
`;

// Estado terminal/erro/sucesso na pagina publica.
export const PublicState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 28px 16px;

  > svg {
    color: var(--pub-accent);
  }
  > h2 {
    font-family: var(--font-display);
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    color: var(--ink);
  }
  > p {
    font-size: 14px;
    color: var(--ink-3);
    margin: 0;
    max-width: 440px;
    line-height: 1.5;
  }
`;

// ============================================================
// ABA DESIGN (controles + preview ao vivo)
// ============================================================

export const DesignLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 420px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const DesignControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;

export const DesignPreview = styled.div`
  position: sticky;
  top: 16px;
  min-width: 0;

  @media (max-width: 1100px) {
    position: static;
  }
`;

export const DesignPreviewBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;

  > .titulo {
    font-family: var(--font-display);
    font-size: 14px;
    font-weight: 600;
  }
  > .modos {
    display: inline-flex;
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    overflow: hidden;
  }
  > .modos button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: 0;
    background: var(--surface);
    color: var(--ink-2);
    font: inherit;
    font-size: 12px;
    cursor: pointer;
  }
  > .modos button + button {
    border-left: 1px solid var(--border-strong);
  }
  > .modos button.is-on {
    background: var(--accent);
    color: var(--ink-on-accent);
  }
  > .modos svg {
    width: 13px;
    height: 13px;
  }
`;

// Moldura do preview: rola por dentro e, no modo celular, estreita para a
// largura de um telefone (o data-viewport da PublicPage faz o resto).
export const DesignViewport = styled.div`
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: auto;
  height: min(76vh, 900px);
  width: ${({ $mobile }) => ($mobile ? "390px" : "100%")};
  max-width: 100%;
  margin: 0 auto;
  background: var(--bg);
  scrollbar-width: thin;
  transition: width 0.2s ease;
`;

// Agrupa controles para desligar todos de uma vez (`disabled`) no modo
// leitura (FORM-41), sem a borda e o espaco padrao do fieldset.
export const EditorFieldset = styled.fieldset`
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: inherit;
`;
